import createBootError from 'owa-shared-start/lib/createBootError';
import getGlobalSettingsAccountMailboxInfo from 'owa-account-source-list-store/lib/selectors/getGlobalSettingsAccountMailboxInfo';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import initializeDateTime from 'owa-shared-bootstrap/lib/initializeDateTime';
import initializeSatchelMiddleware from 'owa-shared-bootstrap/lib/initializeSatchelMiddleware';
import initializeWindowTitle from 'owa-shared-bootstrap/lib/initializeWindowTitle';
import isFeatureEnabled from 'owa-feature-flags/lib/utils/isFeatureEnabled';
import React from 'react';
import setCoprincipalAccountBootState from 'owa-account-source-list-store/lib/actions/setCoprincipalAccountBootState';
import setUserConfiguration from 'owa-session-store/lib/actions/setUserConfiguration';
import { addAnalyticsCallbacks } from 'owa-analytics-start';
import { addIfNotNull } from './utils/addIfNotNull';
import { asyncRender } from './utils/asyncRender';
import { BootState } from 'owa-account-source-list-store/lib/store/schema/BootState';
import { doesClientBuildMatch, getClientVersion, getOpxHostData } from 'owa-config';
import { ErrorBoundary } from 'mini-error-boundary';
import { getLocalizationContext } from 'owa-shared-bootstrap/lib/getLocalizationContext';
import { getWithinGovernor, lazyGovern } from 'owa-tti';
import { hasQueryStringParameter } from 'owa-querystring';
import { initializeApplicationSettings } from 'owa-application-settings';
import { initOwaAccountSourceListStore } from 'owa-account-source-list-api/lib/initOwaAccountSourceListStore';
import { isUserIdle } from 'owa-shared-bootstrap/lib/isUserIdle';
import { lazySetupSharedPostBoot } from 'mini-shared-post-boot';
import { logUsage } from 'owa-analytics';
import { markFunction, trackBottleneck } from 'owa-performance';
import { type OwaWorkload, setOwaWorkload } from 'owa-workloads';
import { registerCreateServiceResponseCallback as registerOwsCreateServiceResponseCallback } from 'owa-service/lib/fetchWithRetry';
import { registerCreateServiceResponseCallback as registerOwsPrimeCreateServiceResponseCallback } from 'owa-ows-gateway/lib/registerCreateServiceResponseCallback';
import { setAccountConfiguration } from 'owa-session-store/lib/actions/setAccountConfiguration';
import { setBundlingConfig } from 'owa-bundling';
import { setupFeatureFlags } from './utils/setupFeatureFlags';
import { updateServiceConfig } from 'owa-service/lib/config';
import type { BootstrapOptions } from 'owa-shared-bootstrap/lib/types/BootstrapOptions';
import type { MailboxInfo } from 'owa-client-types';
import type { PromiseWithKey } from 'owa-performance';
import type { SessionData } from 'owa-service/lib/types/SessionData';

/**
 *  Bootstraps the application
 *  See [README.md]() for details on boot flow
 */
export default markFunction(sharedBootstrap, 'btsp');

function sharedBootstrap(options: BootstrapOptions) {
    setBundlingConfig(
        logUsage,
        task => lazyGovern.importAndExecute(task).then(results => results[0]),
        getWithinGovernor
    );

    addAnalyticsCallbacks(
        registerOwsCreateServiceResponseCallback,
        registerOwsPrimeCreateServiceResponseCallback
    );

    setOwaWorkload(options.workload || 0);
    initializeSatchelMiddleware();

    updateServiceConfig({
        isUserIdle,
        isFeatureEnabled: (f: string, mailboxInfo?: MailboxInfo) =>
            /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
             * See aka.ms/mini-lint-errors
             *	> Unexpected any. Specify a different type. */
            isFeatureEnabled(f as any, mailboxInfo, true),
    });

    return options.sessionPromise.then(async function bootstrap(sessionData: SessionData) {
        // sessionData should never be undefined in the bootstrap flow. It will only be undefined
        // when switching modules

        // Since poisoning the build means to restart the client with a bO query string parameter
        // we don't want to do it if we already have the query string parameter present.
        if (doesClientBuildMatch(sessionData.skipBuilds) && !hasQueryStringParameter('bO')) {
            throw createBootError(
                new Error(`PoisonedBuild ${getClientVersion()}`),
                'PoisonedBuild'
            );
        }

        setupFeatureFlags(sessionData, options);

        if (isFeatureEnabled('fwk-increaseStackTraceLimit')) {
            Error.stackTraceLimit = 20;
        }

        // We want to minimize the amount of time that the account source list store is not initialized
        // so we initialize it as soon as we have the session data
        const sourceId = initOwaAccountSourceListStore(sessionData.owaUserConfig);

        initializeApplicationSettings(sessionData.applicationSettings);

        // NOTE: setUserConfiguration needs to happen after initializeApplicationSettings to make sure the flights
        // and applications settings are initialized when userConfigurationSet is called
        setUserConfiguration(sessionData.owaUserConfig);

        setCoprincipalAccountBootState(sourceId, BootState.StartupComplete);

        setAccountConfiguration(
            'StartupData',
            getGlobalSettingsAccountMailboxInfo(),
            JSON.parse(JSON.stringify(sessionData.owaUserConfig)),
            false /* workaround for indexer values not always being unique: this is known in this context to be the global account */
        );

        const preRenderPromises: PromiseWithKey<unknown>[] = options.prerenderPromises || [];
        const hostPromise = getOpxHostData();
        if (hostPromise) {
            preRenderPromises.push({ promise: hostPromise, key: 'hp' });
        }

        const { locale, culture, dir } = getLocalizationContext(getUserConfiguration());
        preRenderPromises.push({
            promise: options
                .initializeLocalization(locale, culture, dir)
                .catch(e => {
                    addIfNotNull(e, 'source', 'InitLoc');
                    addIfNotNull(e, 'status', e.httpStatus);
                    return Promise.reject(e);
                })
                .then(() => {
                    initializeWindowTitle(options.getWindowTitle);
                }),
            key: 'lcl',
        });

        // Make sure shouldInitializeTimeZoneAnonymously is a boolean as undefined
        // will not initialize the time zones
        const dateTimePromise = initializeDateTime(
            !!options.shouldInitializeTimeZoneAnonymously,
            options.getTimeZoneOverrides
        );
        if (dateTimePromise && options.waitForDateTimeStore) {
            preRenderPromises.push({ promise: dateTimePromise, key: 'dt' });
        }

        // Start the application
        const initializeStatePromise = options.initializeState(sessionData);
        if (initializeStatePromise) {
            preRenderPromises.push({
                promise: initializeStatePromise,
                key: 'ist',
            });
        }

        // Await any additional promises before final render
        await trackBottleneck('ren', preRenderPromises);

        // Return a Promise to render the initial app state
        const renderPromise = markFunction(asyncRender, 'ren')(
            <ErrorBoundary
                fullErrorComponent={options.fullErrorComponent}
                onError={options.onComponentError}
                windowIcons={options.windowIcons}>
                {options.renderMainComponent()}
            </ErrorBoundary>,
            document.getElementById('app')
        );

        const postLazyAction = options.postLazyAction;
        if (postLazyAction) {
            /* eslint-disable-next-line prefer-spread --
             * undefined
             *	> Use the spread operator instead of '.apply()'. */
            postLazyAction.importAndExecute.apply(postLazyAction, options.postLazyArgs || []);
        }

        // shared post boot execution
        const analyticsOptions = options.analyticsOptions;
        lazySetupSharedPostBoot.importAndExecute(analyticsOptions, options.swConfig);

        return renderPromise;
    });
}
